<template>
  <div
    class="raster-layer-row"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <label class="rl-label">
      <input
        v-sync-checked="isActive"
        :type="scope.exclusive ? 'radio' : 'checkbox'"
        class="rl-check"
        :name="scope.exclusive ? `scope-${scope.scopeId}` : undefined"
        @change="activateNode(scope.scopeId, node.id, scope.exclusive)"
      />
      <span class="rl-name">{{ node.raster_layer.name }}</span>
    </label>
    <button
      v-show="hovered || isSwipeTarget"
      class="swipe-btn"
      :class="{ 'swipe-btn--active': isSwipeTarget }"
      :title="isSwipeTarget ? 'Összehasonlító réteg eltávolítása' : 'Összehasonlító rétegnek beállít'"
      @click.stop="toggleSwipe"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="3" x2="12" y2="21"/>
        <polyline points="7 8 4 11 7 14"/>
        <polyline points="17 8 20 11 17 14"/>
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useLayerSelection } from '@/composables/useLayerSelection'
import type { SelectionScope } from '@/composables/layerScope'
import { vSyncChecked } from '@/directives/syncChecked'
import type { LayerTreeRasterLayerNode } from '@/types/layerTree'

const props = defineProps<{
  node: LayerTreeRasterLayerNode
  /** Selection scope this node belongs to (see composables/layerScope.ts). */
  scope: SelectionScope
}>()

const { swipeTarget, activateNode, isNodeActive, setSwipeTarget } = useLayerSelection()

const isActive = computed(() => isNodeActive(props.scope.scopeId, props.node.id))

const hovered = ref(false)

const isSwipeTarget = computed(() => swipeTarget.value?._key === `rl-${props.node.id}`)

function toggleSwipe() {
  if (isSwipeTarget.value) {
    setSwipeTarget(null)
  } else {
    const rl = props.node.raster_layer
    setSwipeTarget({
      _key: `rl-${props.node.id}`,
      layer_type: rl.layer_type,
      url: rl.url,
      wms_layers: rl.wms_layers || undefined,
      wms_format: rl.wms_format || undefined,
      attributions: rl.attributions || undefined,
      opacity: rl.opacity,
      ordering: 9000,
    })
  }
}
</script>

<style scoped>
.raster-layer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  padding: 4px 4px;
  transition: background 0.1s;
}
.raster-layer-row:hover { background: rgba(137,180,250,0.07); }

.rl-label { display: flex; align-items: center; gap: 7px; cursor: pointer; flex: 1; }
.rl-check { accent-color: #a6e3a1; cursor: pointer; }
.rl-name { font-size: 12px; color: #cdd6f4; }

.swipe-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: 1px solid #313244;
  border-radius: 6px;
  color: #6c7086;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s, background 0.12s;
}
.swipe-btn:hover { color: #89b4fa; border-color: #89b4fa; background: rgba(137,180,250,0.1); }
.swipe-btn--active {
  color: #a6e3a1 !important;
  border-color: #a6e3a1 !important;
  background: rgba(166,227,161,0.12) !important;
}
</style>
